import { useState } from 'react';
import { Button, Input } from 'antd';
import type { ProgramQuestion } from '../../features/aiPlanner/plannerApi';

interface Props {
  questions: ProgramQuestion[];
  onBack?: () => void;
  onContinue: (answers: Record<string, string>) => void;
  loading: boolean;
}

/** add_program — two or three combined questions, answered in plain text. */
export default function QuestionsStep({ questions, onContinue, loading }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const answered = questions.filter((q) => (answers[q.id] ?? '').trim()).length;

  return (
    <div className="flex flex-col gap-4 py-1">
      <div>
        <h3 className="text-base font-semibold text-ink">A few things before the plan</h3>
        <p className="mt-1 text-sm leading-[22px] text-ink-secondary">
          Short answers are fine. Leave a question blank if you don't know yet — the AI will make a sensible assumption and say so.
        </p>
      </div>
      <ol className="flex flex-col gap-3">
        {questions.map((q, i) => (
          <li key={q.id} className="rounded-lg border border-line-soft bg-white p-3">
            <div className="flex gap-2">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-shade text-xs font-semibold text-brand">{i + 1}</span>
              <p className="text-sm font-medium leading-6 text-ink">{q.text}</p>
            </div>
            <Input.TextArea className="mt-2" autoSize={{ minRows: 2, maxRows: 5 }} disabled={loading}
              placeholder="Your answer" value={answers[q.id] ?? ''}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          </li>
        ))}
      </ol>
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-ink-secondary">{answered} of {questions.length} answered</span>
        <Button type="primary" loading={loading} onClick={() => onContinue(answers)}>Propose the changes</Button>
      </div>
    </div>
  );
}
